import React, { useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, Keyboard } from 'swiper/modules';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const reviews = [
  {
    name: 'Kusum Diwakar',
    role: 'Marketing Lead, KD Crafts',
    text:
      'Professional, creative, and results‑focused. Clear communication, timely delivery, and thoughtful work across SEO and social. Better visibility and engagement; the website feels cleaner and easier for customers to use.',
  },
  {
    name: 'Viraj Sharma',
    role: 'Product Manager, Vistara Homes',
    text:
      'Understood our needs and delivered without overpromising. Honest communication, simple process, and practical suggestions. Felt like working with a teammate who cares and knows marketing well.',
  },
  {
    name: 'Naveen Jain',
    role: 'Director, NJ Trading Co.',
    text:
      'Performance‑focused team with dependable delivery. They strengthened our brand online, improved lead flow, and kept timelines tight. Website updates and tracking were handled carefully and consistently.',
  },
  {
    name: 'Jaspreet Singh',
    role: 'Operations Head, JS Logistics',
    text:
      'Trustworthy and reliable. Clear vision, listened to requirements, and delivered on time. Responsive team with good service quality; work aligns with our goals and is easy to maintain.',
  },
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  // Premium easing constant for smooth feel
  const premiumEasing = [0.16, 1, 0.3, 1];

  // Double the reviews for perfect loop stability if we have few slides
  // This ensures Swiper has enough clones to handle the 'centeredSlides' logic
  // without jumping or skipping indices when loop=true.
  const displayReviews = useMemo(() =>
    reviews.length < 8 ? [...reviews, ...reviews] : reviews,
    []);

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#fafafa] to-white relative overflow-hidden select-none">
      {/* Subtle Background Elements - Updated to Theme Colors */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-l from-purple-100 to-transparent rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-50 to-purple-50 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: premiumEasing }}
          >
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
              What Our Clients Say
            </h2>
            <p className="text-slate-500 text-base md:text-lg mt-3 max-w-xl mx-auto font-medium">
              Genuine feedback from real clients who have worked with us.
            </p>
          </motion.div>
        </div>

        <div className="relative max-w-[1240px] mx-auto px-4 md:px-12">
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Autoplay, Pagination, Navigation, Keyboard]}
            spaceBetween={24}
            slidesPerView={1}
            centeredSlides={true}
            loop={true}
            loopedSlides={reviews.length}
            grabCursor={true}
            watchSlidesProgress={true}
            roundLengths={true}
            observer={true}
            observeParents={true}
            speed={800}
            keyboard={{ enabled: true }}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{
              clickable: true,
              el: '.custom-dot-pagination',
            }}
            navigation={{
              prevEl: '.nav-prev',
              nextEl: '.nav-next',
            }}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex % reviews.length)}
            breakpoints={{
              640: {
                slidesPerView: 1.2,
                spaceBetween: 20
              },
              860: {
                slidesPerView: 2,
                spaceBetween: 24
              },
              1100: {
                slidesPerView: 3,
                spaceBetween: 30
              },
            }}
            className="testimonial-swiper !py-10"
          >
            {displayReviews.map((review, idx) => (
              <SwiperSlide key={idx} className="!h-auto flex items-center justify-center">
                {({ isActive }) => (
                  <motion.div
                    animate={{
                      scale: isActive ? 1 : 0.94,
                      opacity: isActive ? 1 : 0.4,
                      filter: isActive ? 'blur(0px)' : 'blur(1px)',
                    }}
                    whileHover={{
                      y: -8,
                      scale: isActive ? 1.02 : 0.96,
                      transition: { duration: 0.4, ease: "easeOut" }
                    }}
                    transition={{ duration: 0.6, ease: premiumEasing }}
                    className={`
                      relative w-full max-w-[400px] bg-white border rounded-2xl p-7 md:p-9
                      transition-all duration-500 flex flex-col h-full group
                      ${isActive
                        ? 'shadow-[0_20px_50px_-15px_rgba(124,58,237,0.12)] z-20 border-purple-100 hover:border-purple-300'
                        : 'shadow-sm z-10 border-slate-100'
                      }
                    `}
                  >
                    {/* Active Floating Effect */}
                    {isActive && (
                      <motion.div
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-x-0 top-0 h-full pointer-events-none"
                      />
                    )}

                    <div className="flex-grow">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-600"></span>
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest group-hover:text-purple-500 transition-colors">
                          Google Review
                        </span>
                      </div>

                      <blockquote className="text-slate-700 text-[15px] md:text-base leading-relaxed font-medium mb-8">
                        {review.text}
                      </blockquote>
                    </div>

                    <div className="pt-6 border-t border-slate-50 mt-auto flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center font-extrabold text-lg shadow-sm group-hover:bg-purple-600 transition-colors duration-500">
                        {review.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <cite className="text-slate-900 font-bold text-[15px] not-italic block">{review.name}</cite>
                        <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">{review.role}</span>
                      </div>
                    </div>

                    {/* Autoplay Progress Bar - Theme Colors */}
                    <div className="absolute bottom-0 left-0 w-full h-[3px] bg-slate-50 overflow-hidden rounded-b-2xl">
                      <AnimatePresence mode="wait">
                        {isActive && (
                          <motion.div
                            key={activeIndex}
                            initial={{ width: "0%" }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 4.8, ease: "linear" }}
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 shadow-[0_0_8px_rgba(168,85,247,0.4)]"
                          />
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Subtle Hover Reveal Line */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-t-2xl origin-left" />
                  </motion.div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Nav Arrows */}
          <button className="nav-prev absolute left-0 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white shadow-lg border border-slate-100 text-slate-800 hover:bg-slate-900 hover:text-white transition-all duration-300 hidden md:flex active:scale-90 hover:scale-110 group/btn">
            <FiChevronLeft className="w-5 h-5 group-hover/btn:-translate-x-0.5 transition-transform" />
          </button>
          <button className="nav-next absolute right-0 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white shadow-lg border border-slate-100 text-slate-800 hover:bg-slate-900 hover:text-white transition-all duration-300 hidden md:flex active:scale-90 hover:scale-110 group/btn">
            <FiChevronRight className="w-5 h-5 group-hover/btn:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Dots */}
        <div className="custom-dot-pagination flex justify-center items-center gap-3 mt-4" />
      </div>

      <style jsx global>{`
        .custom-dot-pagination .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          background: #e2e8f0;
          opacity: 1;
          margin: 0 !important;
          transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
          border-radius: 99px;
          cursor: pointer;
        }
        .custom-dot-pagination .swiper-pagination-bullet-active {
          width: 24px;
          background: #7c3aed;
          box-shadow: 0 0 12px rgba(124, 58, 237, 0.3);
        }
        .testimonial-swiper .swiper-wrapper {
          display: flex;
          align-items: stretch;
        }
        /* Mask effect */
        .testimonial-swiper {
          mask-image: linear-gradient(to right, 
            transparent 0%, 
            black 10%, 
            black 90%, 
            transparent 100%
          );
        }
        @media (max-width: 768px) {
          .testimonial-swiper { mask-image: none; }
        }
      `}</style>
    </section>
  );
};

export default Testimonials;
